// The chat: thread (messages, matches, proposal cards) and composer (text, files with the crop preview, Think harder).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Json } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtDateTime, inr, label } from '@/lib/format';
import { Badge, Button, Loading } from '@/components/ui';
import { useCropGate } from '@/components/Attachments';
import { Md } from './Md';
import { ProposalCard, entityLink } from './ProposalCard';
import { useAssistant, type PendingFile } from './useAssistant';

export function AssistantPanel({ conversationId, onConversation, context, compact }: { conversationId: string | null; onConversation: (id: string) => void; context?: Json | null; compact?: boolean }) {
  const { me } = useAuth();
  const { items, send, stop, busy, status, conversation, loading, refresh } = useAssistant(conversationId, { context, onConversation });
  const [text, setText] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [harder, setHarder] = useState(false);
  const { gate, decide } = useCropGate();
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [items.length, status, (items[items.length - 1] as { text?: string } | undefined)?.text]);
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.url)), [files]);

  const addFiles = async (list: FileList | File[] | null) => {
    if (!list) return;
    for (const file of Array.from(list)) {
      if (file.size > 10 * 1024 * 1024) continue;
      const r = await decide(file, 'receipt_photo');
      if (!r) continue;
      setFiles((xs) => [...xs, { file, blob: r.blob, name: r.blob === file ? file.name : file.name.replace(/\.[^.]+$/, '') + (r.blob.type === 'image/jpeg' ? '.jpg' : ''), cropped: r.cropped, url: URL.createObjectURL(r.blob) }]);
    }
  };
  const submit = async () => {
    if (busy || (!text.trim() && files.length === 0)) return;
    const t = text, f = files; setText(''); setFiles([]);
    await send(t, f, harder); setHarder(false);
  };
  const canChat = !!me;

  return (
    <div className={`ai-panel ${compact ? 'ai-compact' : ''}`}>
      <div className="ai-thread">
        {loading && <Loading />}
        {!conversationId && items.length === 0 && (
          <div className="ai-welcome">
            <p><b>Drop a receipt or ask a question.</b> The assistant checks the books first (by file, reference number, amount and date, party). If something is not recorded it drafts the entry the way the firm records such things; nothing changes until you press Apply.</p>
            <p className="hint">The model reads only what this conversation uploads and masked summaries of the books. Every applied change carries your name and the proposal in the audit log.</p>
          </div>
        )}
        {items.map((it) => {
          if (it.kind === 'message') return (
            <div key={it.key} className={`ai-msg ai-${it.role}`}>
              <div className="ai-bubble">
                {it.role === 'assistant' ? <Md text={it.text || (it.streaming ? '…' : '')} /> : <p>{it.text || <span className="muted">(file only)</span>}</p>}
                {(it.attachments ?? []).length > 0 && <div className="ai-files">{(it.attachments as Json[]).map((a) => <span key={a.id} className="badge tone-muted">{a.filename}</span>)}</div>}
              </div>
              <small className="ai-meta">{it.role === 'assistant' ? (it.model && it.model !== 'template' ? `${it.model}${it.cost != null ? ` · ${inr(it.cost)}` : ''}` : it.model === 'template' ? 'answered from the books, no model' : '') : me?.email === conversation?.started_by ? '' : conversation?.started_by} {fmtDateTime(it.at)}</small>
            </div>
          );
          if (it.kind === 'matches') return (
            <div key={it.key} className="ai-matches">
              <small className="muted">{it.strong ? 'Found in the books' : it.candidates.length ? 'Possible matches (not conclusive)' : 'Nothing like this in the books'}</small>
              {it.candidates.slice(0, 6).map((c, i) => { const to = entityLink(c.entity, c.id); const t: ReactNode = <>{c.number ?? label(c.entity)} · {fmtDate(c.date)} · {inr(c.amount)} <Badge status={c.status} /> <small className="muted">{((c.why ?? []) as string[]).map((w) => w.replace(/_/g, ' ')).join(', ')}</small></>; return <div key={i} className="ai-match">{to ? <Link to={to}>{t}</Link> : t}</div>; })}
            </div>
          );
          if (it.kind === 'proposal') return <ProposalCard key={it.key} p={it.proposal} compact={compact} onChanged={() => refresh()} />;
          return <div key={it.key} className={`ai-note tone-${it.tone}`}>{it.text}</div>;
        })}
        {status && <div className="ai-status"><span className="spinner spinner-sm" /> {status}</div>}
        <div ref={endRef} />
      </div>
      <div className="ai-composer" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void addFiles(e.dataTransfer.files); }}>
        {files.length > 0 && (
          <div className="ai-files">
            {files.map((f, i) => <span key={i} className="ai-file">{f.blob.type.startsWith('image/') ? <img src={f.url} alt={f.name} className="thumb" /> : <span className="thumb" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem' }}>{f.name.slice(0, 16)}</span>}<button type="button" className="toast-x" aria-label="Remove" onClick={() => setFiles((xs) => xs.filter((_, j) => j !== i))}>×</button>{f.cropped && <small className="muted">cropped</small>}</span>)}
          </div>
        )}
        <textarea className="input" rows={compact ? 2 : 3} placeholder={files.length ? 'Anything to add? (optional)' : 'Is this recorded? … or drop a receipt'} value={text} onChange={(e) => setText(e.target.value)} disabled={!canChat || busy}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void submit(); } }} />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="row">
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>Add file</Button>
            <input ref={inputRef} type="file" hidden multiple accept="image/*,application/pdf" onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
            <label className="checkbox" style={{ margin: 0 }}><input type="checkbox" checked={harder} onChange={(e) => setHarder(e.target.checked)} disabled={busy} /> <small>Think harder</small></label>
          </div>
          {busy ? <Button size="sm" variant="ghost" onClick={stop}>Stop</Button> : <Button size="sm" variant="primary" disabled={!canChat || (!text.trim() && files.length === 0)} onClick={() => void submit()}>Send</Button>}
        </div>
        <small className="hint">Documents are read once and matched in the books before any drafting. Photos are cropped to the bill at capture; keep the original if the crop is wrong.</small>
      </div>
      {gate}
    </div>
  );
}
